import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, getLanguage, getLanguages, localeFallbacks, matchLocale, resolveLocale } from './registry';
import { createLocaleRuntime, createTranslationLoader } from './runtime';

export const normalizeLocale = value => matchLocale(value, 'ui');

export const detectLocale = () => {
  const explicit = normalizeLocale(new URLSearchParams(window.location.search).get('lang'));
  if (explicit) return explicit;
  try {
    const saved = normalizeLocale(localStorage.getItem('noteician.ui_locale'));
    if (saved) return saved;
  } catch (_) { /* Storage can be disabled. */ }
  return (navigator.languages || [navigator.language]).map(normalizeLocale).find(Boolean) || DEFAULT_LOCALE;
};

const applyLocale = language => {
  const metadata = getLanguage(language, 'ui');
  document.documentElement.lang = metadata.code;
  document.documentElement.dir = metadata.direction;
  const url = new URL(window.location.href);
  if (url.searchParams.has('lang')) {
    url.searchParams.set('lang', language);
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }
  try { localStorage.setItem('noteician.ui_locale', language); } catch (_) { /* Optional. */ }
};

const runtime = createLocaleRuntime({
  instance: i18n.use(initReactI18next),
  registry: { list: getLanguages, match: matchLocale, resolve: resolveLocale, fallbackChain: localeFallbacks },
  loadTranslation: createTranslationLoader(language => import(`../locales/${language}.json`)),
  onApplied: applyLocale,
});

// Render only after the selected language and its fallback dictionaries load.
// Failed initialization remains retryable; account preference changes await the
// same runtime before recording is enabled.
export const initializeLocale = () => runtime.initialize(detectLocale());
export const i18nReady = initializeLocale();
i18nReady.catch(() => {}); // index.js presents an explicit retry screen on failure.
export const setLocale = (locale, options) => runtime.change(locale, options);
export const formattingLocale = () => getLanguage(i18n.language, 'ui').formatLocale;
export const marketingHomeUrl = (locale = i18n.language) => {
  const language = resolveLocale(locale, 'marketing');
  return `https://www.noteician.com${language === DEFAULT_LOCALE ? '/' : `/${language}/`}`;
};
export const formatMoney = (amount, currency) => new Intl.NumberFormat(formattingLocale(), {
  style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits: amount % 100 === 0 ? 0 : 2,
}).format(amount / 100);

export default i18n;
